import { EntityProfileType } from "../../../types/entity";
import { AccountType } from "./accounts";
import {
    ChartOfAccountsItemNameStringOrFunction,
    defaultCashChartOfAccounts,
    getSubtypeBordersInCoAOrThrow,
} from "./chartOfAccounts";

interface KickDefaultAccountFields {
    isManuallyCreated: boolean;
    financialAccountId: number | null;
    kickOriginalCode: number | null;
}

export function isKickDefaultAccount<T extends KickDefaultAccountFields>(
    account: T,
): account is T & { kickOriginalCode: number } {
    return (
        !account.isManuallyCreated &&
        account.financialAccountId === null &&
        account.kickOriginalCode !== null
    );
}

function resolveNameForEntityType(
    name: ChartOfAccountsItemNameStringOrFunction,
    entityType: EntityProfileType,
): string {
    return typeof name === "function" ? name(entityType) : name;
}

export interface DefaultKickAccount {
    name: string;
    type: AccountType;
}

function buildDefaultAccountsByCode(
    entityType: EntityProfileType,
): Map<number, DefaultKickAccount> {
    const accountsByCode = new Map<number, DefaultKickAccount>();

    for (const type of defaultCashChartOfAccounts) {
        const subtypes = [
            ...type.subtypesStartFromBeginning,
            ...type.subtypesStartFromEnd,
        ];
        for (const subtype of subtypes) {
            if (subtype.if && !subtype.if(entityType)) {
                continue;
            }
            if (!subtype.accounts) {
                continue;
            }
            let currentCode = getSubtypeBordersInCoAOrThrow(
                entityType,
                type.typeKey,
                subtype.key,
            ).startFrom;
            for (const account of subtype.accounts) {
                if (account.if && !account.if(entityType)) {
                    continue;
                }
                accountsByCode.set(currentCode, {
                    name: resolveNameForEntityType(account.name, entityType),
                    type: type.typeKey,
                });
                currentCode++;
            }
        }
    }

    return accountsByCode;
}

const defaultAccountsByEntityType = new Map<
    EntityProfileType,
    Map<number, DefaultKickAccount>
>();

export function getDefaultAccountsByCode(
    entityType: EntityProfileType,
): Map<number, DefaultKickAccount> {
    const cached = defaultAccountsByEntityType.get(entityType);
    if (cached) {
        return cached;
    }
    const accountsByCode = buildDefaultAccountsByCode(entityType);
    defaultAccountsByEntityType.set(entityType, accountsByCode);
    return accountsByCode;
}

interface ResolveDefaultAccountNameParams {
    kickOriginalCode: number;
    entityType: EntityProfileType;
}

// entityType is required because codes overlap across ledger types (302000 is
// "Owner Capital" for SoleProp, "Capital" for Partnership).
export function resolveDefaultAccountName({
    kickOriginalCode,
    entityType,
}: ResolveDefaultAccountNameParams): string | null {
    return (
        getDefaultAccountsByCode(entityType).get(kickOriginalCode)?.name ?? null
    );
}
